/**
 * Phase 64 — CarHive Analytics
 *
 * Tiny, GDPR-friendly GA4 wrapper.
 *
 *  • GA4 (gtag.js) is loaded LAZILY and ONLY after the user has granted
 *    `analytics: true` consent. We never call `gtag` before then.
 *  • Events fired WITHOUT consent are silently dropped — they are not
 *    queued for later, because re-firing pageviews from a stale buffer
 *    would inflate the GA4 user-journey. Events fired AFTER consent but
 *    before the gtag.js script has finished loading are queued in
 *    `_queue` and flushed once the script is ready.
 *  • Events fired *without* a configured `REACT_APP_GA4_ID` are silently
 *    dropped (no analytics endpoint to send to anyway).
 *  • UTM params (utm_source/medium/campaign/term/content) are extracted
 *    from the URL on first load and persisted to sessionStorage so they
 *    can ride along with later events.
 *
 *  Consent is exposed via `getConsent()/setConsent()`; the consent UI
 *  lives in <CookieConsentBanner /> and persists to
 *  localStorage["ch_consent_v1"].
 */

const GA_ID = process.env.REACT_APP_GA4_ID || "";
const CONSENT_KEY = "ch_consent_v1";
const UTM_KEY = "ch_utm_v1";

let gtagLoaded = false;
let gtagLoading = null;
const _queue = [];

// ---- Consent ----

/**
 * @returns {{analytics:boolean, decided:boolean, ts:number|null}}
 */
export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return { analytics: false, decided: false, ts: null };
    const parsed = JSON.parse(raw);
    return {
      analytics: !!parsed.analytics,
      decided: !!parsed.decided,
      ts: parsed.ts || null,
    };
  } catch {
    return { analytics: false, decided: false, ts: null };
  }
}

export function setConsent({ analytics }) {
  const payload = { analytics: !!analytics, decided: true, ts: Date.now() };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(payload)); } catch { /* ignore quota */ }
  if (analytics) {
    _ensureGtag();
  }
  // Notify listeners (e.g. banner can hide itself)
  try { window.dispatchEvent(new CustomEvent("ch:consent", { detail: payload })); } catch { /* ignore */ }
}

// ---- UTM persistence ----

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

export function captureUtmFromUrl() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const present = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) present[k] = v;
    }
    if (Object.keys(present).length === 0) return;
    sessionStorage.setItem(UTM_KEY, JSON.stringify({ ...present, captured_at: Date.now() }));
  } catch { /* ignore */ }
}

export function getUtm() {
  try {
    const raw = sessionStorage.getItem(UTM_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// ---- GA4 loader (lazy) ----

function _ensureGtag() {
  if (!GA_ID) return null;
  if (gtagLoaded) return window.gtag;
  if (gtagLoading) return gtagLoading;

  gtagLoading = new Promise((resolve) => {
    // dataLayer + stub gtag must exist before script
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, {
      // We do client-side route tracking manually for SPA navigations,
      // so disable GA4's automatic page_view to avoid duplicates.
      send_page_view: false,
      anonymize_ip: true,
    });

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
    s.onload = () => {
      gtagLoaded = true;
      // Flush queue
      while (_queue.length) {
        const [evtName, params] = _queue.shift();
        try { window.gtag("event", evtName, params); } catch { /* ignore */ }
      }
      resolve(window.gtag);
    };
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return gtagLoading;
}

// ---- Public API ----

/** Track a custom event. Buffers if GA4 hasn't loaded yet. */
export function trackEvent(name, params = {}) {
  if (!name) return;
  const utm = getUtm();
  const enriched = {
    ...(utm || {}),
    ...params,
  };
  // Strip undefined / null values for cleanliness
  Object.keys(enriched).forEach((k) => {
    if (enriched[k] === undefined || enriched[k] === null) delete enriched[k];
  });

  // Always queue; flush when GA4 is ready
  if (!GA_ID) return; // analytics disabled at build
  const consent = getConsent();
  if (!consent.analytics) return; // user has not opted in

  if (gtagLoaded && window.gtag) {
    try { window.gtag("event", name, enriched); } catch { /* ignore */ }
    return;
  }
  _queue.push([name, enriched]);
  _ensureGtag();
}

/** Track an SPA route change as a page_view. */
export function trackPageview({ path, title } = {}) {
  if (!path) return;
  trackEvent("page_view", {
    page_path: path,
    page_title: title || (typeof document !== "undefined" ? document.title : undefined),
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
    language: typeof document !== "undefined" ? document.documentElement.lang : undefined,
  });
}

/** True if analytics is fully active (env set + consent given + script ready or loading). */
export function isAnalyticsActive() {
  return !!GA_ID && getConsent().analytics;
}

/** Useful for the banner / debug UI. */
export function getAnalyticsConfig() {
  return { ga_id: GA_ID, enabled: !!GA_ID };
}
